import { useState, useCallback } from 'react';
import { message } from 'antd';
import { j201Api, FJ110, FJ111 } from '../../../services/j201Api';
import { Mode } from './useJ201Mode';

const emptyDetail = (): Partial<FJ111> => ({
  fj111_02: 1,
  fj111_10: '', fj111_11: '', fj111_12: '',
  fj111_121: '', fj111_122: '', fj111_12a: '',
  fj111_13: 0, fj111_14: '',
  fj111_15: 0, fj111_151: 0,
  fj111_16: 0, fj111_161: 0,
  fj111_17: '',
});

export function useJ201Record() {
  const [record, setRecord] = useState<FJ110 | null>(null);
  const [formData, setFormData] = useState<Partial<FJ110>>({});
  const [details, setDetails] = useState<Partial<FJ111>[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string): Promise<{
    data: FJ110;
    dets: Partial<FJ111>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await j201Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const setField = useCallback((key: keyof FJ110, value: FJ110[keyof FJ110]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const getField = useCallback((key: keyof FJ110): FJ110[keyof FJ110] => {
    return (formData as Record<string, FJ110[keyof FJ110]>)[key] ?? '';
  }, [formData]);

  const getDetailField = useCallback((idx: number, key: keyof FJ111): FJ111[keyof FJ111] => {
    const d = details[idx];
    if (!d) return '';
    return (d as Record<string, FJ111[keyof FJ111]>)[key] ?? '';
  }, [details]);

  const setDetailField = useCallback((idx: number, key: keyof FJ111, value: FJ111[keyof FJ111]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FJ110) => void,
  ) => {
    setLoading(true);
    try {
      // T-F05: reindex detail rows 1-based
      const reindexed = details.map((d, i) => ({ ...d, fj111_02: i + 1 }));
      const req = {
        master: formData as FJ110,
        details: reindexed as FJ111[],
      };
      let result: FJ110;
      if (mode === 'add') {
        result = await j201Api.create(req);
        message.success(`新增成功: ${result.fj110_01}`);
      } else {
        result = await j201Api.update(formData.fj110_01 as string, req);
        message.success('修改成功');
      }
      // T-F01: use mutation response directly
      onSuccess(result);
    } catch (err: unknown) {
      // T-F06: handle 422 validation errors
      const axiosErr = err as { response?: { status?: number; data?: { errors?: Array<{ message: string }> } } };
      if (axiosErr.response?.status === 422 && axiosErr.response.data?.errors) {
        const msgs = axiosErr.response.data.errors.map(e => e.message).join('\n');
        message.error({ content: msgs, duration: 5 });
      } else {
        const errMsg = err instanceof Error ? err.message : '儲存失敗';
        message.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  const handleDelete = useCallback(async (onSuccess: () => void) => {
    if (!record?.fj110_01) return;
    setLoading(true);
    try {
      await j201Api.delete(record.fj110_01);
      message.success('刪除成功');
      onSuccess();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record]);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({
      fj110_80: 'N',
      fj110_15: 1,
      fj110_171: 1,
      fj110_16: false,
      fj110_17: true,
      fj110_33: false,
      fj110_18: 0,
      fj110_181: 0,
      fj110_19: 0,
    });
    setDetails([]);
  }, []);

  const applyResult = useCallback((result: FJ110) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  const addDetailRow = useCallback(() => {
    setDetails(prev => {
      const row = emptyDetail();
      row.fj111_02 = prev.length + 1;
      return [...prev, row];
    });
  }, []);

  const deleteDetailRow = useCallback((idx: number) => {
    // T-F05: reindex after delete
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((d, i) => ({ ...d, fj111_02: i + 1 }));
    });
  }, []);

  return {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  };
}
